import * as yup from 'yup';

// Constants

import { INVALID_FILE_FORMAT_MESSAGE, MAX_FILE_SIZE, MAX_FILE_SIZE_MESSAGE } from '../../../../config/constants';

// Utilities

import { getNumberFromStringValueRange, isValidFileType } from '../../../../utilities/helper-functions';

export const validationSchema = [
  yup.object().shape({
    productFunds: yup.array().min(1, 'Necesita seleccionar al menos un producto').of(
      yup.object({
        totalIngresos: yup.number(),
        cantDepositos: yup.number().required('Este campo es requerido').typeError('Favor ingresar un número').test("cantDepositosTest", "Favor ingresar un número entero.", function (value) {
          return !String(value)?.includes('.');
        }),
        cantRetiros: yup.number().required('Este campo es requerido').typeError('Favor ingresar un número').test("cantRetirosTest", "Favor ingresar un número entero.", function (value) {
          return !String(value)?.includes('.');
        }),
        montoPromedio: yup.string().required('Este campo es requerido'),
        descripcionMontDep: yup.string().required('Este campo es requerido').test("depositosIngresos", "El monto de deposito no puede ser mayor a sus ingresos.", function (value) {
          return this.parent.totalIngresos >= getNumberFromStringValueRange(value);
        }),
        descripcionMontRet: yup.string().required('Este campo es requerido').test("depositosRetiros", "El rango de retiros no puede ser mayor al de depositos.", function (value) {
          return getNumberFromStringValueRange(this.parent.descripcionMontDep) > (getNumberFromStringValueRange(value) - 0.1);
        }),
        motivoApertura: yup.string().required('Este campo es requerido'),
        origenFondos: yup.string().required('Este campo es requerido'),
        destinoFondos: yup.string().required('Este campo es requerido'),
        paisOrigen: yup.string().required('Este campo es requerido'),
        paisDestino: yup.string().required('Este campo es requerido'),
        usaMonedaVirtual: yup.string().required('Este campo es requerido'),
        monedaVirtual: yup.string().when("usaMonedaVirtual", {
          is: (val) => val === 'si',
          then: yup.string().required('Este campo es requerido'),
          otherwise: yup.string(),
        }),
      }),
    ),
    movilizacionProductos: yup.object().shape({
      entidad: yup.string().required('Este campo es requerido'),
      ciudad: yup.string().required('Este campo es requerido'),
    }),
    cliente: yup.object().shape({
      oficinaAsesor: yup.string().required('Este campo es requerido'),
    }),
  }),
  yup.object().shape({
    recaudos: yup.object().shape({
      documentoIdentidad: yup.mixed().when("tipoDocumento", {
        is: (val) => val !== "P",
        then: yup.mixed().required('Este campo es requerido').test("documentoIdentidadFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('documentoIdentidadFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed()
      }),
      documentoPasaporte: yup.mixed().when("tipoDocumento", {
        is: (val) => val == "P",
        then: yup.mixed().required('Este campo es requerido').test("documentoPasaporteFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('documentoPasaporteFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed()
      }),
      constanciaResidenciaRif: yup.mixed().when("hasLevelOneAccount", {
        is: (val) => val === "no",
        then: yup.mixed().required('Este campo es requerido').test("constanciaResidenciaRifFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('constanciaResidenciaRifFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed()
      }),
      constanciaEmpleo: yup.mixed().when(["hasLevelOneAccount", "hasJob", "caracteristicaCliente"], {
        is: (hasLevelOneAccount, hasJob, caracteristicaCliente) => hasLevelOneAccount === 'no' && hasJob === 'si' && !['1', '4', '9'].includes(caracteristicaCliente),
        then: yup.mixed().required('Este campo es requerido').test("constanciaEmpleoFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('constanciaEmpleoFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed()
      }),
      constanciaIngreso: yup.mixed().when(["hasLevelOneAccount", "hasIncome", "hasBusiness", "tipoCliente"], {
        is: (hasLevelOneAccount, hasIncome, hasBusiness, tipoCliente) => hasLevelOneAccount === 'no' && (hasIncome === 'si' || hasBusiness === 'si') && (tipoCliente === '1' || tipoCliente === '2'),
        then: yup.mixed().required('Este campo es requerido').test("constanciaIngresoFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('constanciaIngresoFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed(),
      }),
      constanciaOrdenTribunal: yup.mixed().when(["hasLevelOneAccount", "motivoApertura"], {
        is: (hasLevelOneAccount, motivoApertura) => motivoApertura == '18' && hasLevelOneAccount === 'no',
        then: yup.mixed().required('Este campo es requerido').test("constanciaOrdenTribunalFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('constanciaOrdenTribunalFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed(),
      }),
      constanciaAutorizacionOperarMercado: yup.mixed().when(["hasLevelOneAccount", "caracteristicaCliente"], {
        is: (hasLevelOneAccount, caracteristicaCliente) => ['6'].includes(caracteristicaCliente) && hasLevelOneAccount === 'no',
        then: yup.mixed().required('Este campo es requerido').test("constanciaAutorizacionOperarMercadoFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('constanciaAutorizacionOperarMercadoFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed(),
      }),
      constanciaCarnetLaboral: yup.mixed().when(["hasLevelOneAccount", "caracteristicaCliente"], {
        is: (hasLevelOneAccount, caracteristicaCliente) => ['5'].includes(caracteristicaCliente) && hasLevelOneAccount === 'no',
        then: yup.mixed().required('Este campo es requerido').test("constanciaCarnetLaboralFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('constanciaCarnetLaboralFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed(),
      }),
      constanciaCedulaRepresentanteLegal: yup.mixed().when(["hasLevelOneAccount", "caracteristicaCliente"], {
        is: (hasLevelOneAccount, caracteristicaCliente) => ['4'].includes(caracteristicaCliente) && hasLevelOneAccount === 'no',
        then: yup.mixed().required('Este campo es requerido').test("constanciaCedulaRepresentanteLegalFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('constanciaCedulaRepresentanteLegalFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed(),
      }),
      constanciaDesignacionLegalComoRepresentante: yup.mixed().when(["hasLevelOneAccount", "caracteristicaCliente"], {
        is: (hasLevelOneAccount, caracteristicaCliente) => ['4'].includes(caracteristicaCliente) && hasLevelOneAccount === 'no',
        then: yup.mixed().required('Este campo es requerido').test("constanciaDesignacionLegalComoRepresentanteFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('constanciaDesignacionLegalComoRepresentanteFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed(),
      }),
      constanciaComplementoFatca: yup.mixed().when(["hasLevelOneAccount", "tieneRelacion"], {
        is: (hasLevelOneAccount, tieneRelacion) => tieneRelacion == 'si' && hasLevelOneAccount === 'no',
        then: yup.mixed().required('Este campo es requerido').test("constanciaComplementoFatcaFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('constanciaComplementoFatcaFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed(),
      }),
      constanciaDeclaracionJuradaDeIngresos: yup.mixed().when(["hasLevelOneAccount", "tipoCliente"], {
        is: (hasLevelOneAccount, tipoCliente) => ['5', '6'].includes(tipoCliente) && hasLevelOneAccount === 'no',
        then: yup.mixed().required('Este campo es requerido').test("constanciaDeclaracionJuradaDeIngresosFileSize", MAX_FILE_SIZE_MESSAGE, function (value) {
          if (value?.size) {
            return value?.size <= MAX_FILE_SIZE;
          } else {
            return true;
          }
        }).test('constanciaDeclaracionJuradaDeIngresosFormat', INVALID_FILE_FORMAT_MESSAGE, (value) => {
          return isValidFileType(value && value.name.toLowerCase(), 'extensions')
        }),
        otherwise: yup.mixed(),
      }),
    }),    
  }),
  yup.object().shape({
    terms: yup.string().required('Favor aceptar los términos y condiciones'),
  }),
  yup.object().shape({}),
];